import cn from "classnames";
import { createRequestForUploadFile } from "helpers/form";
import { isTechSupport } from "helpers/hiddenData";
import { useRouter } from "hooks/useRouter";
import interact from "interactjs";
import MessagesIcon from "messages.svg";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { CustomScroll } from "react-custom-scroll";
import SignalRConnection from "services/signalRConnection";
import {
    getHasUnreadMessages,
    getMessages,
    PER_PAGE,
    sendFileMessage,
    sendMessage,
    setMessagesAsRead,
} from "sources/techSupportChat";
import Message from "./message";

const CHAT_WIDTH = 400;
const CHAT_HEIGHT = 600;
const CHAT_VIEWPORT_PADDING = 10;
const CHAT_DEFAULT_BOTTOM_OFFSET = 30;
const TECH_SUPPORT_CHAT_POSITION_KEY = "tech-support-chat-position";

const isValidPosition = (position) =>
    position && Number.isFinite(position.x) && Number.isFinite(position.y);

const getDefaultPosition = () => {
    if (typeof window === "undefined") {
        return { x: CHAT_VIEWPORT_PADDING, y: CHAT_VIEWPORT_PADDING };
    }

    return {
        x: Math.max(CHAT_VIEWPORT_PADDING, window.innerWidth - CHAT_WIDTH - CHAT_VIEWPORT_PADDING),
        y: Math.max(CHAT_VIEWPORT_PADDING, window.innerHeight - CHAT_HEIGHT - CHAT_DEFAULT_BOTTOM_OFFSET),
    };
};

const getStoredPosition = () => {
    try {
        const position = JSON.parse(window.localStorage.getItem(TECH_SUPPORT_CHAT_POSITION_KEY));
        return isValidPosition(position) ? position : null;
    } catch {
        return null;
    }
};

const savePosition = (position) => {
    try {
        window.localStorage.setItem(TECH_SUPPORT_CHAT_POSITION_KEY, JSON.stringify(position));
    } catch {
        // The chat remains movable when localStorage is unavailable.
    }
};

export default function TechSupportChat(props) {
    const isTechSupportUser = isTechSupport();
    const { location } = useRouter();
    const [mounted, setMounted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [showDescription, setShowDescription] = useState(true);
    const [showButton, setShowButton] = useState(location.pathname.includes("registration"));
    const [messages, setMessages] = useState([]);
    const [content, setContent] = useState("");
    const [page, setPage] = useState(0);
    const [isLastPage, setIsLastPage] = useState(false);
    const [dragHighlight, setDragHighlight] = useState(false);
    const [position, setPosition] = useState(() => getStoredPosition() ?? getDefaultPosition());
    const scrollContainer = useRef();
    const inputField = useRef();
    const fileInput = useRef();
    const chatOpened = useRef();
    const chatContainer = useRef();
    const positionRef = useRef(position);

    const clampPosition = useCallback((nextPosition) => {
        const { width = CHAT_WIDTH, height = CHAT_HEIGHT } = chatContainer.current?.getBoundingClientRect() ?? {};
        const maxX = Math.max(CHAT_VIEWPORT_PADDING, window.innerWidth - width - CHAT_VIEWPORT_PADDING);
        const maxY = Math.max(CHAT_VIEWPORT_PADDING, window.innerHeight - height - CHAT_VIEWPORT_PADDING);

        return {
            x: Math.min(Math.max(nextPosition.x, CHAT_VIEWPORT_PADDING), maxX),
            y: Math.min(Math.max(nextPosition.y, CHAT_VIEWPORT_PADDING), maxY),
        };
    }, []);

    const updatePosition = useCallback((nextPosition) => {
        positionRef.current = nextPosition;
        setPosition(nextPosition);
    }, []);

    useEffect(() => {
        if (isTechSupportUser) {
            return;
        }
        setShowButton(location.pathname.includes("registration"));
    }, [isTechSupportUser, location.pathname]);

    const handleScroll = (e) => {
        if (e.target.scrollTop < 50 && loaded) {
            getNextItems();
        }
    };

    const getNextItems = () => {
        if (isLastPage || loading) {
            return;
        }
        setLoading(true);
        getMessages(page).then((res) => {
            const scrollPos = scrollContainer.current.state.scrollPos;
            const prevContentHeight = scrollContainer.current.contentHeight;
            setMessages((prev) => [...res.reverse(), ...prev]);
            if (res.length < PER_PAGE) {
                setIsLastPage(true);
            }
            setPage((prev) => prev + 1);
            setTimeout(() => {
                scrollContainer.current.updateScrollPosition(
                    scrollContainer.current.contentHeight - prevContentHeight + scrollPos,
                );
                setLoading(false);
            }, 0);
        });
    };

    useEffect(() => {
        if (isTechSupportUser) {
            return;
        }
        getHasUnreadMessages().then((res) => res && props.setOpened(true));
        const connection = new SignalRConnection({
            url: "/signalr/techSupportChat",
            actions: [
                {
                    name: "Send",
                    action: (newMessage) => {
                        chatOpened.current ? setMessages((prev) => [...prev, newMessage]) : props.setOpened(true);
                    },
                },
            ],
            joinActionName: "Join",
            leaveActionName: "Leave",
        });
        return () => connection.disconnect();
    }, [isTechSupportUser]);

    useEffect(() => {
        if (isTechSupportUser) {
            return;
        }
        chatOpened.current = props.opened;
        if (props.opened) {
            setMounted(true);
            setMessagesAsRead();
            getNextItems();
            const timeoutId = setTimeout(() => {
                inputField.current?.focus();
                scrollContainer.current?.updateScrollPosition(Number.MAX_SAFE_INTEGER);
                setLoaded(true);
            }, 1000);
            return () => clearTimeout(timeoutId);
        } else {
            setMessages([]);
            setPage(0);
            setLoaded(false);
            setIsLastPage(false);
        }
    }, [isTechSupportUser, props.opened]);

    useEffect(() => {
        if (!props.opened || !mounted) {
            return;
        }

        const updatePositionWithinViewport = () => {
            const nextPosition = clampPosition(positionRef.current);
            updatePosition(nextPosition);
            savePosition(nextPosition);
        };

        updatePositionWithinViewport();
        window.addEventListener("resize", updatePositionWithinViewport);
        return () => window.removeEventListener("resize", updatePositionWithinViewport);
    }, [clampPosition, mounted, props.opened, updatePosition]);

    useEffect(() => {
        if (!props.opened || !mounted || !chatContainer.current) {
            return;
        }

        const draggable = interact(chatContainer.current).draggable({
            allowFrom: ".ts-chat__header",
            ignoreFrom: ".ts-chat__close",
            listeners: {
                move: (event) => {
                    updatePosition(
                        clampPosition({
                            x: positionRef.current.x + event.dx,
                            y: positionRef.current.y + event.dy,
                        }),
                    );
                },
                end: () => savePosition(positionRef.current),
            },
        });

        return () => draggable.unset();
    }, [clampPosition, mounted, props.opened, updatePosition]);

    if (isTechSupportUser) {
        return null;
    }

    const closeOrders = () => props.setOpened(false);

    const eventsHandler = (event) => {
        const { key, ctrlKey, shiftKey } = event;
        if (key === "Enter") {
            event.preventDefault();
            if (ctrlKey || shiftKey) {
                setContent(`${content}\n`);
            } else if (content.length) {
                handleSubmit();
            }
        }
        setTimeout(() => {
            recalcFieldHeight();
        }, 100);
    };

    const handleSubmit = () => {
        setLoading(true);
        sendMessage({ content, sentFromMonitoringActivationPage: location.pathname.includes("registration") }).then(
            () => {
                setContent("");
                setTimeout(() => {
                    scrollContainer.current?.updateScrollPosition(Number.MAX_SAFE_INTEGER);
                    recalcFieldHeight();
                }, 0);
                setLoading(false);
                setTimeout(() => {
                    inputField.current?.focus();
                }, 100);
            },
        );
    };

    const handleButtonClick = () => {
        if (content.length > 0) {
            handleSubmit();
        } else {
            fileInput.current.click();
        }
    };

    const recalcFieldHeight = () => {
        inputField.current.style.height = "1px";
        inputField.current.style.height = inputField.current.scrollHeight + 1 + "px";
        inputField.current.scrollTop = inputField.current.scrollHeight;
    };

    const sendFiles = (files) => {
        if (files.length === 0) {
            return;
        }
        const filesArr = Array.from(files);
        if (filesArr.length > 5) {
            return alert({ type: "warning", message: "Доступна загрузка не более 5 файлов", title: "Ошибка" });
        }
        if (files.length > 5) {
            return alert({ type: "warning", message: "Доступна загрузка не более 5 файлов", title: "Ошибка" });
        }
        setLoading(true);
        return new Promise((resolve) => {
            const sendFile = (i) => {
                const file = files[i];
                const paramsRequest = createRequestForUploadFile("attachedFile", file);
                paramsRequest.body.append(
                    "sentFromMonitoringActivationPage",
                    location.pathname.includes("registration"),
                );
                sendFileMessage(paramsRequest).then(() => {
                    if (files[i + 1]) {
                        sendFile(i + 1);
                    } else {
                        setLoading(false);
                        resolve();
                    }
                });
            };

            sendFile(0);
        });
    };

    const eventDefault = (e, eventName) => {
        e.preventDefault();
        e.stopPropagation();

        switch (eventName) {
            case "onDragEnter":
            case "onDragOver":
                return setDragHighlight(true);
            case "onDragLeave":
                return setDragHighlight(false);
            case "onDrop": {
                setDragHighlight(false);
                const files = Array.from(e.dataTransfer.files);
                return sendFiles(files);
            }
        }
    };

    const events = {
        onDragEnter: (e) => eventDefault(e, "onDragEnter"),
        onDragOver: (e) => eventDefault(e, "onDragOver"),
        onDragLeave: (e) => eventDefault(e, "onDragLeave"),
        onDrop: (e) => eventDefault(e, "onDrop"),
    };

    const handleOnPaste = (event) => {
        const files = (event.clipboardData || event.originalEvent.clipboardData).files;
        sendFiles(files);
    };

    const sendFilesFromInput = () => {
        const files = Array.from(fileInput.current.files);
        sendFiles(files);
    };

    const hideDescription = (e) => {
        e.stopPropagation();
        setShowDescription(false);
    };

    const openChat = () => {
        props.setOpened(true);
        setShowDescription(false);
    };

    if (!props.opened) {
        if (showButton) {
            return (
                <div className="ts-chat__button" onClick={() => openChat()}>
                    <MessagesIcon />
                    {showDescription && (
                        <div className="ts-chat__button-desc">
                            Возникли сложности с подключением мониторинга?
                            <br />
                            Напишите нам, мы поможем!
                            <div className="ts-chat__button-desc--close" onClick={hideDescription}>
                                ×
                            </div>
                        </div>
                    )}
                </div>
            );
        } else {
            return null;
        }
    }

    return (
        <div
            className={cn("ts-chat", { "ts-chat--mounted": mounted })}
            ref={chatContainer}
            style={{ left: position.x, top: position.y }}
            {...events}
        >
            <div className="ts-chat__header">
                <div className="text-center">Напишите нам, мы онлайн!</div>
                <div className="ts-chat__close" onClick={closeOrders} />
            </div>
            <div className={cn("ts-chat__body", { "loading loading--small": loading || !loaded })}>
                <CustomScroll keepAtBottom ref={scrollContainer} heightRelativeToParent="100%" onScroll={handleScroll}>
                    <div className="ts-chat__items-wrap">
                        {messages.length === 0 && (
                            <div className="ts-chat__empty-message">
                                Мы онлайн!
                                <br />
                                Напишите нам, мы поможем!
                            </div>
                        )}
                        {messages.map((item) => (
                            <Message key={item.id} {...item} />
                        ))}
                    </div>
                </CustomScroll>
            </div>
            <div className="ts-chat__footer">
                <div className="d-flex">
                    <div className="ts-chat__input-wrap">
                        <textarea
                            autoFocus
                            disabled={loading}
                            ref={inputField}
                            className="form-control form-control-sm ts-chat__input"
                            placeholder="Введите сообщение"
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            onKeyDown={eventsHandler}
                            onPaste={handleOnPaste}
                            rows={1}
                        />
                        <input ref={fileInput} type="file" multiple onChange={sendFilesFromInput} className="d-none" />
                        <button
                            className={cn("ts-chat__input-btn ts-chat__input-btn--send", {
                                "icon-uEA15-send": content.length > 0,
                                "icon-uEA0A-file": content.length === 0,
                            })}
                            onClick={handleButtonClick}
                        />
                    </div>
                </div>
            </div>
            <div className={cn("ts-chat__file-drop", { "ts-chat__file-drop--show": dragHighlight })}>
                <div className="ts-chat__file-drop--content">
                    <i className="icon-uEA09-document" />
                    <br />
                    Отпустите, чтобы отправить файл
                </div>
            </div>
        </div>
    );
}
